import { EventTypes } from '@candulabs/craft';

import {
  BuiltInComponents,
  focusableNodes,
  isRootNode,
  nodesWithFocusableChild,
} from 'craft/constants';
import { useEditor, useSelection } from 'craft/hooks';

/**
 * A list of queries shared by all the various shortcuts
 */
export const useShortcutQueries = () => {
  const { query } = useEditor();

  const getFocus = () => {
    const focusedElements = query.getEvent(EventTypes.focused).all();
    return focusedElements.length > 0;
  };

  const { selection, nextFocusNodeId } = useSelection();

  const getFocusNodeId = (nodeName: string): string | null => {
    if (!focusableNodes.includes(nodeName) && !nodesWithFocusableChild.includes(nodeName)) {
      return null;
    }

    if (focusableNodes.includes(nodeName)) {
      return selection.last; // select the current node if it's focusable
    }

    return !selection.isEmpty ? nextFocusNodeId(selection.last) : null; // otherwise select its closest focusable child
  };

  const noSelection = () => selection.isEmpty && !getFocus();
  const isInSelectionMode = () => !selection.isEmpty && !getFocus();
  const isInFocusMode = () => !selection.isEmpty && !!getFocus();

  const isSelectedAndIsNotRoot = () => isInSelectionMode() && !isRootNode(selection.last);

  const isDeletable = () =>
    !selection.isEmpty &&
    !isRootNode(selection.last) &&
    !selection.all.some((nodeId: string) => !query.node(nodeId).isDeletable());

  const isTypographyNode = () =>
    !selection.isEmpty &&
    query.node(selection.last).get()?.data?.name === BuiltInComponents.Typography;

  return {
    getFocusNodeId,
    noSelection,
    isInFocusMode,
    isInSelectionMode,
    isSelectedAndIsNotRoot,
    isDeletable,
    isTypographyNode,
  };
};
